import "./styles.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { keepConnectReturn } from "./connect/search.ts";
import { ApiRequestError } from "./lib/api.ts";
import { keepConfirmLink } from "./lib/confirm-link.ts";
import { I18nProvider, startCopy, warmCopy } from "./lib/i18n.tsx";
import { installIosHistory } from "./lib/ios-history.ts";
import { startErrorReporting } from "./lib/sentry.ts";
import { startSidebar } from "./lib/sidebar.ts";
import { preloadOnIntent, staleWhileRevalidate, startFirstScreen } from "./lib/snappy.ts";
import { endSplashOnFirstScreen } from "./lib/splash.ts";
import { reloadOnStaleBuild } from "./lib/stale-build.ts";
import { startTheme } from "./lib/theme.ts";
import { routeTree } from "./routeTree.gen.ts";

// A trip's way home in a popup Adea opened (Google, an app's sign-in, GitHub, Slack): the answer goes to the window
// that opened it and this one closes, without starting the app. Anywhere else this is over at once.
// Its code comes only with such an address, so it is no part of every first load.
if (new URLSearchParams(window.location.search).has("trip")) {
  const { answerOpener } = await import("./lib/trip-return.ts");
  if (await answerOpener()) await new Promise<never>(() => {});
}

// A tab from before a deploy that can't fetch a piece of the old build loads the new one.
reloadOnStaleBuild();

// A confirm link's signed request leaves the address before the router reads it, so it can't travel on into sign-in.
keepConfirmLink();
// A trip to Google, an app's sign-in or GitHub came back: its answer leaves the address the same way, kept for the
// connect panel it was for.
keepConnectReturn();

// The copy comes in its own chunk: ask for it first, so it travels with the first screen's code.
startCopy();

/**
 * The cache lives in memory only: never persisted to browser storage, so nothing is left behind on a
 * shared computer and nothing stale shows after a visit. The server is the only source of truth; the
 * live channel refetches what changed (and everything shown when it reconnects), so coming back to the
 * tab doesn't refetch every screen at once. A page seen in the last half hour opens from memory and
 * refreshes behind the scenes (`staleWhileRevalidate`).
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 30 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failures, error) => {
        if (error instanceof ApiRequestError && error.status >= 400 && error.status < 500) return false;
        return failures < 2;
      },
    },
    mutations: { retry: false },
  },
});

staleWhileRevalidate(queryClient);

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// The installed iOS app keeps no history behind a top-level page, so iOS's edge swipe "back" can't fight the drawer.
installIosHistory(router);

// The saved appearance choice is applied before the first paint.
startTheme();
startSidebar();

const root = document.getElementById("root");
if (!root) throw new Error("#root is missing from index.html");

// The words of the screen being opened (or about to be, on hover) are fetched with its code.
warmCopy(router.latestLocation.pathname);
router.subscribe("onBeforeNavigate", (event) => warmCopy(event.toLocation.pathname));
const preloadRoute = router.preloadRoute.bind(router);
router.preloadRoute = ((options: Parameters<typeof preloadRoute>[0]) => {
  warmCopy(router.buildLocation(options as never).pathname);
  return preloadRoute(options);
}) as typeof router.preloadRoute;

// The first screen's code is fetched while the session is still being checked, not after; a plain link (the
// shell's nav, a row's link) fetches its page's code and data when the pointer rests on it, like `<Link>` does.
startFirstScreen(router, queryClient);
// The launch splash (index.html) gives way once the first screen is on the page.
endSplashOnFirstScreen(router);
preloadOnIntent(router);

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={null}>
        <I18nProvider>
          <RouterProvider router={router} />
        </I18nProvider>
      </Suspense>
    </QueryClientProvider>
  </StrictMode>,
);

// Errors reach Sentry when the server names a project; loaded after the first screen.
void startErrorReporting();
